import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CardContent } from '@mui/material';
import { Alert, TransparentCard } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useAPIClientRequest } from 'c-hooks';
import { apiClient } from 'c-data';
import { useSelector } from 'react-redux';
import { authSelectors } from 'c-auth-module/Slices';
import ReactGA from 'react-ga';

/**
 * Consent screen for an MCP client asking to act as the signed-in user.
 *
 * The API sends the browser here after validating the authorization request, and everything this
 * page needs arrives in the query string. It renders who is asking and who they would be acting as,
 * then posts the same parameters back so the API can mint the authorization code.
 *
 * Nothing here decides anything. The API re-validates every parameter on the way back in -- by the
 * time they reach this page they are a query string, which is to say user input again -- so a
 * tampered value fails there rather than being trusted because this page displayed it.
 *
 * It lives behind the normal post-auth guard on purpose. An unauthenticated visitor is redirected to
 * the login page in-app, which preserves this route and its query string in router state; arriving
 * at /login from outside the app would lose them.
 */
const AuthorizeMcpPage = () => {
    const t = useCommonTranslation;
    const user = useSelector(authSelectors.authenticatedUser);
    const [redirecting, setRedirecting] = useState(false);

    useEffect(() => {
        ReactGA.pageview(window.location.pathname);
    }, []);

    const params = useMemo(() => {
        const search = new URLSearchParams(window.location.search);

        return {
            client_id: search.get('client_id') ?? '',
            redirect_uri: search.get('redirect_uri') ?? '',
            state: search.get('state') ?? '',
            code_challenge: search.get('code_challenge') ?? '',
            resource: search.get('resource') ?? '',
        };
    }, []);

    const { start, error, isLoading } = useAPIClientRequest(apiClient.Auth.approveMcpAuthorization);

    const onApprove = useCallback(async () => {
        try {
            // start() resolves with the response and throws on failure; the hook has already put
            // the message into `error` by then, so there is nothing to do in the catch.
            const response = await start(params);

            if (response?.data?.redirect_to) {
                // The client is listening on a loopback port for this redirect. It is not an in-app
                // route, so the router must not handle it.
                setRedirecting(true);
                window.location.href = response.data.redirect_to;
            }
        } catch {
            setRedirecting(false);
        }
    }, [start, params]);

    const onCancel = useCallback(() => {
        window.close();
    }, []);

    // A direct visit has none of these. Say so rather than posting an empty approval and showing
    // the user an opaque error from the API.
    const incomplete = !params.client_id || !params.redirect_uri || !params.code_challenge;

    return (
        <TransparentCard title={t('Modules.Admin.AuthorizeMcp.metaTitle')}>
            <CardContent>
                {incomplete && (
                    <Alert severity="error">{t('Modules.Admin.AuthorizeMcp.incomplete')}</Alert>
                )}
                {!incomplete && (
                    <>
                        <Box mb={2}>
                            {t('Modules.Admin.AuthorizeMcp.prompt', { email: user?.email ?? '' })}
                        </Box>
                        <Box mb={2}>{t('Modules.Admin.AuthorizeMcp.scopeExplanation')}</Box>
                        {error && (
                            <Alert severity="error">{t('Modules.Admin.AuthorizeMcp.failed')}</Alert>
                        )}
                        <Box display="flex" gap={2}>
                            <Button
                                variant="contained"
                                onClick={onApprove}
                                disabled={isLoading || redirecting}
                            >
                                {t('Modules.Admin.AuthorizeMcp.approve')}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={onCancel}
                                disabled={isLoading || redirecting}
                            >
                                {t('Modules.Admin.AuthorizeMcp.cancel')}
                            </Button>
                        </Box>
                    </>
                )}
            </CardContent>
        </TransparentCard>
    );
};

const setup: PageConfig = {
    id: 'AuthorizeMcpPage',
    component: AuthorizeMcpPage,
    systemSearch: {
        title: 'Modules.Admin.AuthorizeMcp.metaTitle',
        description: 'Modules.Admin.AuthorizeMcp.description',
        type: 'page',
        route: PostAuthRoutes.Admin.AuthorizeMcp,
        // Only ever reached by following a redirect from the API with a live authorization request
        // in the query string, so it is not somewhere to return to.
        saveAsRecentPage: false,
        breadcrumbs: [{ label: 'Modules.Admin.AuthorizeMcp.metaTitle' }],
    },
};

export default setup;
